/**
 * Label field mapper — maps API `data_points` entries to QR / text fields.
 *
 * Data-point format (comma-separated):
 *   "MatID, Country, CropName, QRCode for MatID, QRCode for ContainerID"
 *
 * • Entries prefixed with "QRCode for" or "QR Code for" are QR fields.
 * • Everything else is a text field.
 * • Text entries whose name matches a QR target are excluded (they are
 *   already shown as text beside the QR code).
 */

// ---------------------------------------------------------------------------
// Regex that matches QR-code data-point prefixes
// Handles "QRCode for X", "QR Code for X", "QR code for X", etc.
// ---------------------------------------------------------------------------
const QR_PREFIX_RE = /^QR\s*Code\s+for\s+/i;

/** Check whether a data-point entry represents a QR code */
export const isQrField = (entry: string): boolean =>
    QR_PREFIX_RE.test(entry);

/** Extract the referenced field name from a QR data-point entry */
export const parseQrTarget = (entry: string): string =>
    entry.replace(QR_PREFIX_RE, '').trim();

// ---------------------------------------------------------------------------
// Row-data key mapper
// ---------------------------------------------------------------------------

/**
 * Map of **lowercased** template data-point names → candidate row-data keys.
 * The first key found in `rowData` wins.
 */
const DATAPOINT_ROW_KEYS: Record<string, string[]> = {
    'matid':        ['MatID', 'matId', 'mat_id'],
    'mat id':       ['MatID', 'matId', 'mat_id'],
    'country':      ['country', 'Country', 'site_code', 'site', 'Site'],
    'cropname':     ['crop', 'CropName', 'cropName', 'crop_name'],
    'crop name':    ['crop', 'CropName', 'cropName', 'crop_name'],
    'containerid':  ['container_alt_id', 'ContainerID', 'containerId', 'container_id'],
    'container id': ['container_alt_id', 'ContainerID', 'containerId', 'container_id'],
    'admincode':    ['AdminCode', 'adminCode', 'admin_code', 'Admin Code', 'Admin code'],
    'admin code':   ['AdminCode', 'adminCode', 'admin_code', 'Admin Code', 'Admin code'],
    'sapnr':        ['SAPNR', 'sapnr', 'sap_nr'],
    'treatment':    ['treatment', 'Treatment', 'treatments', 'Treatments'],
    'splot id':     ['splot_id', 'splotId'],
    'splot name':   ['splot_name', 'splotName'],
    'site':         ['site_code', 'site', 'Site'],
    'trial id':     ['trial_bid', 'trial_id', 'trialId'],
    'trail id':     ['trial_bid', 'trial_id', 'trialId'],
    'trial bid':    ['trial_bid', 'trial_id', 'trialId'],
    'trial_bid':    ['trial_bid', 'trial_id', 'trialId'],
    'traid id':     ['trial_bid', 'trial_id', 'trialId'],
    'traid bid':    ['trial_bid', 'trial_id', 'trialId'],
    'traid_bid':    ['trial_bid', 'trial_id', 'trialId'],
    'status':       ['status', 'Status'],
    'priority':     ['priority', 'Priority'],
};

// ---------------------------------------------------------------------------
// Display-name mapper
// ---------------------------------------------------------------------------

/** Human-readable display labels keyed by lowercased template data-point name */
const DATAPOINT_DISPLAY: Record<string, string> = {
    'matid':        'Material ID',
    'mat id':       'Material ID',
    'country':      'Country',
    'cropname':     'Crop',
    'crop name':    'Crop',
    'containerid':  'Container ID',
    'container id': 'Container ID',
    'admincode':    'Admin Code',
    'admin code':   'Admin Code',
    'sapnr':        'SAP Nr',
    'treatment':    'Treatment',
    'splot id':     'SP Lot ID',
    'splot name':   'SP Lot Name',
    'site':         'Site',
    'trial id':     'Trial ID',
    'trail id':     'Trial ID',
    'trial bid':    'Trial ID',
    'trial_bid':    'Trial ID',
    'traid id':     'Trial ID',
    'traid bid':    'Trial ID',
    'traid_bid':    'Trial ID',
    'status':       'Status',
    'priority':     'Priority',
};



// ---------------------------------------------------------------------------
// Resolve helpers
// ---------------------------------------------------------------------------

/** Resolve a template data-point name to its display label */
const resolveDisplayName = (fieldName: string): string => {
    const norm = fieldName.toLowerCase().trim();
    return DATAPOINT_DISPLAY[norm] ?? fieldName;
};

/** Resolve a template data-point name to a row-data value */
const resolveFieldValue = (fieldName: string, rowData: Record<string, any>): string => {
    const safeString = (val: unknown): string => {
        if (val == null || val === '') return '';
        // Guard: never stringify raw arrays/objects — they produce "[object Object]".
        // An array here means the key was never normalized (e.g. raw Treatments before
        // normalizeMaterialRowData ran). Return empty so the label shows nothing.
        if (Array.isArray(val)) return '';
        if (typeof val === 'object') return '';
        return String(val);
    };

    // 1. Direct key match
    if (fieldName in rowData && rowData[fieldName] != null && rowData[fieldName] !== '') {
        return safeString(rowData[fieldName]);
    }

    // 2. Mapped candidates
    const norm = fieldName.toLowerCase().trim();
    const candidates = DATAPOINT_ROW_KEYS[norm];
    if (candidates) {
        for (const key of candidates) {
            if (key in rowData && rowData[key] != null && rowData[key] !== '') {
                return safeString(rowData[key]);
            }
        }
    }

    // 3. Case-insensitive fallback (strip spaces/underscores for fuzzy match)
    const stripped = norm.replace(/[\s_]/g, '');
    for (const key of Object.keys(rowData)) {
        if (key.toLowerCase().replace(/[\s_]/g, '') === stripped) {
            return safeString(rowData[key] ?? '');
        }
    }

    return '';
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ParsedField {
    key: string;
    label: string;
    value: string;
}

export interface ParsedLabelFields {
    qrFields: ParsedField[];   // max 2
    textFields: ParsedField[];
}

// ---------------------------------------------------------------------------
// Core parser
// ---------------------------------------------------------------------------

/**
 * Parse a comma-separated `data_points` string against a row object.
 * Returns up to 2 QR fields and all remaining text fields.
 *
 * QR entries are detected by the "QRCode for <field>" / "QR Code for <field>" prefix.
 * Text entries whose name matches a QR target are excluded to avoid duplication
 * (they are already rendered as text beside the QR code).
 *
 * Fields come from `data_points` only — nothing is auto-added or stripped by the UI.
 * For material labels, Admin Code and Trial ID keep their placement rules when present.
 */
export const parseLabelDataPoints = (
    dataPoints: string | undefined | null,
    rowData: Record<string, any>,
    isMaterialMode: boolean = true,
): ParsedLabelFields => {
    if (!dataPoints) {
        return { qrFields: [], textFields: [] };
    }

    const entries = dataPoints
        .split(/[,&]/)
        .map((k) => k.trim())
        .filter(Boolean);

    // First pass — collect the normalized names of all fields that have a QR entry
    // Normalize by lowercasing AND stripping spaces/underscores so "Mat ID" matches "MatID"
    const normalize = (s: string) => s.toLowerCase().replace(/[\s_]/g, '');
    const qrTargetNames = new Set<string>();
    for (const entry of entries) {
        if (isQrField(entry)) {
            qrTargetNames.add(normalize(parseQrTarget(entry)));
        }
    }

    const qrFields: ParsedField[] = [];
    const textFields: ParsedField[] = [];

    for (const entry of entries) {
        if (isQrField(entry)) {
            // "QRCode for MatID" → QR field whose value comes from the MatID row key
            if (qrFields.length < 2) {
                const target = parseQrTarget(entry);
                const value = resolveFieldValue(target, rowData);
                const label = resolveDisplayName(target);
                qrFields.push({ key: target, label, value });
            }
        } else {
            // Skip text entries that duplicate a QR target (shown beside the QR code)
            if (qrTargetNames.has(normalize(entry))) continue;

            const value = resolveFieldValue(entry, rowData);
            const label = resolveDisplayName(entry);
            textFields.push({ key: entry, label, value });
        }
    }

    // Material labels keep Trial ID last; every other field stays in data_points order.
    if (isMaterialMode) {
        const normalizeKey = (s: string) => s.toLowerCase().replace(/[\s_]/g, '');
        const trialKeySet = new Set(['trialid', 'trailid', 'trialbid', 'traidid', 'traidbid']);

        const takeField = (keys: Set<string>): ParsedField | null => {
            let picked: ParsedField | null = null;
            for (let i = textFields.length - 1; i >= 0; i--) {
                if (keys.has(normalizeKey(textFields[i].key))) {
                    picked = textFields[i];
                    textFields.splice(i, 1);
                }
            }
            return picked;
        };

        const trialField = takeField(trialKeySet);
        if (trialField) textFields.push(trialField);
    }

    return { qrFields, textFields };
};

/** Split text fields into a top half and bottom half for two-row label layout */
export const splitTextFields = (textFields: ParsedField[]): { topText: ParsedField[]; bottomText: ParsedField[] } => {
    const half = Math.ceil(textFields.length / 2);
    return { topText: textFields.slice(0, half), bottomText: textFields.slice(half) };
};

/**
 * Material label data-point mapper.
 * Maps template `data_points` keys → API response keys.
 *
 * Template expects: container_alt_id, crop, site, matId, trial_id, treatment
 * API response keys: update the values below to match the actual API response.
 */
export const MATERIAL_DATAPOINT_API_KEY_MAP: Record<string, string> = {
    container_alt_id: '',                // direct from validateMatId response
    crop: '[0].crop_xrd_oid',            // resolved to crop name via crops list
    site: 'template.site_code',          // from validateMatId template
    matId: '[0].MatID',                  // from fetchMaterialDetails array
    trial_id: '[0].trial_bid',           // from fetchMaterialDetails array (/material_details)
    treatment: '[0].Treatments',         // from fetchMaterialDetails array (stringified below)
};

/**
 * Resolve a nested property path like 'template.site_code' or '[0].MatID'
 * against an object. Returns undefined if any segment is missing.
 */
const getNestedValue = (obj: any, path: string): any => {
    if (!obj || !path) return undefined;
    // Split on '.' but keep bracket segments like '[0]' attached
    const segments = path.replace(/\[(\d+)]/g, '.$1').split('.').filter(Boolean);
    let current = obj;
    for (const seg of segments) {
        if (current == null) return undefined;
        current = current[seg];
    }
    return current;
};

/**
 * Normalize API response data for Material labels.
 * Uses MATERIAL_DATAPOINT_API_KEY_MAP to copy API response values
 * into the keys expected by the template's data_points.
 *
 * Supports nested property paths (e.g. 'template.site_code', '[0].MatID').
 * Only copies when the mapper value is non-empty and the target key
 * is not already present in the row data.
 *
 * @param crops — optional RefCrop[] from globalState; when provided,
 *   the `crop` field is resolved from crop_xrd_oid code to crop name.
 */
export const normalizeMaterialRowData = (
    rowData: Record<string, any>,
    crops: any[] = [],
    sites: any[] = [],
): Record<string, any> => {
    const normalized = { ...rowData };

    // Flatten array responses — fetchMaterialDetails returns an array
    // When spread into an object, array items become keys '0', '1', etc.
    // Copy top-level keys from the first element into normalized if not already present
    const firstItem = normalized['0'] ?? (Array.isArray(rowData) ? rowData[0] : null);
    if (firstItem && typeof firstItem === 'object' && !Array.isArray(firstItem)) {
        for (const [k, v] of Object.entries(firstItem)) {
            // Overwrite if key is absent OR existing value is null/empty — material_details
            // values must win over null placeholders that may arrive from the validation response.
            if (!(k in normalized) || normalized[k] == null || normalized[k] === '') {
                normalized[k] = v;
            }
        }
    }

    // Apply the explicit API key map — fill missing keys OR overwrite null/empty placeholders
    for (const [templateKey, apiKey] of Object.entries(MATERIAL_DATAPOINT_API_KEY_MAP)) {
        if (apiKey && apiKey !== templateKey && (!normalized[templateKey] || normalized[templateKey] == null || normalized[templateKey] === '')) {
            const val = getNestedValue(normalized, apiKey);
            if (val != null && val !== '') {
                normalized[templateKey] = val;
            }
        }
    }

    // Ensure site_code is populated (Country maps to this)
    if (!normalized.site_code) {
        normalized.site_code = getNestedValue(normalized, 'template.site_code')
            ?? normalized.splotDetails?.site_code
            ?? '';
    }

    // Ensure container_alt_id is populated at top level
    if (!normalized.container_alt_id) {
        const containers = normalized.Containers ?? normalized.containers;
        if (Array.isArray(containers) && containers.length > 0) {
            normalized.container_alt_id = containers[0].container_alt_id ?? '';
        }
    }

    // Ensure MatID is populated at top level
    if (!normalized.MatID) {
        normalized.MatID = normalized.matId ?? normalized.mat_id ?? '';
    }

    // Ensure trial_id is populated from /material_details trial_bid first.
    if (!normalized.trial_id) {
        normalized.trial_id = normalized.trial_bid;
    }

    // Stringify treatment if it's an array of objects (e.g. [{treatment_name: "T1", should_print: true}, ...])
    // Filter treatments by should_print: true if the property exists; otherwise include all
    const treatmentVal = normalized.treatment ?? normalized.Treatments ?? normalized.treatments;
    if (Array.isArray(treatmentVal)) {
        normalized.treatment = treatmentVal
            .filter((t: any) => {
                // If should_print is explicitly defined, use it; otherwise include by default
                if (typeof t === 'object' && t !== null && 'should_print' in t) {
                    return t.should_print === true;
                }
                return true; // Include if should_print is not defined
            })
            .map((t: any) => (typeof t === 'string' ? t : t?.treatment_name ?? t?.name ?? ''))
            .filter(Boolean)
            .join(', ');
        // Always clear the raw array keys so resolveFieldValue cannot fall through
        // to them and produce "[object Object],[object Object]" when all treatments
        // have should_print: false (resulting in an empty string above).
        delete normalized.Treatments;
        delete normalized.treatments;
    } else if (treatmentVal && !normalized.treatment) {
        normalized.treatment = typeof treatmentVal === 'string' ? treatmentVal : String(treatmentVal);
    }

    // Resolve site_code → country name using the sites reference list
    if (sites.length > 0 && normalized.site_code) {
        const siteMatch = sites.find((s: any) => s.site_code === normalized.site_code || s.full_code === normalized.site_code);
        if (siteMatch?.country) {
            normalized.country = siteMatch.country;
        }
    }

    // Resolve crop code → crop name using the crops reference list
    if (crops.length > 0) {
        const cropCode = normalized.crop
            ?? getNestedValue(normalized, 'template.crop_code')
            ?? normalized.crop_xrd_oid
            ?? getNestedValue(normalized, '[0].crop_xrd_oid');
        if (cropCode) {
            const match = crops.find((c: any) => c.crop_xrd_oid === cropCode);
            if (match?.name) {
                normalized.crop = match.name;
            }
        }
    }
    return normalized;
};
